import promiseFinally from 'promise-finally';
import pTimeout from 'p-timeout';
import eventHelper from './eventHelper';
import VideoElementFacadeFactory from './video_element/index';
import isVideoFrameBlackDefault from './isVideoFrameBlack';
import defaultEnv from './env';

// eslint-disable-next-line no-unused-vars
const typeVideoElementFacade = VideoElementFacadeFactory();

/**
 * Wait for the video to be playing
 * @param {Object} params
 *  @property {typeVideoElementFacade} videoElementFacade
 *  @property {Number} maxWaitTime
 *  @property {Boolean} isBufferedStream - whether or not the video is a buffered stream (has a video track, we're subscribing to video)
 *  @property {Object} deps - dependencies for the function, used for unit testing
 *
 * @returns Promise<Boolean> true if we think the video is playing, false if we timeout
 */
function waitForVideoToBePlaying({
  defaultVideoElementFacade,
  maxWaitTime = 5000,
  isBufferedStream = false,
  deps = {},
}) {
  const env = deps.env || defaultEnv;
  const videoElementFacade = deps.videoElementFacade || defaultVideoElementFacade;
  const isVideoFrameBlack = deps.isVideoFrameBlack || isVideoFrameBlackDefault;
  const videoElementFacadeEvents = deps.videoElementFacadeEvents || eventHelper(videoElementFacade);

  const promise = new Promise((resolve) => {
    // eslint-disable-next-line consistent-return
    const onVideoPlaybackHandler = (videoPlaybackEvent) => {
      if (!isBufferedStream || !env.isChromium) {
        // If there is no video and we're not subscribed to video, or if we're using a non-Chromium browser,
        // resolve immediately because the video frame callback will never be called or because the bug is not present on non-Chromium browsers.
        return resolve(videoPlaybackEvent);
      }
      const videoFrameCallback = (_, metadata) => {
        // During an AMR transition or any new stream bind, we check that a real video frame is displayed for Chromium browsers
        // before switching streams to eliminate the screen flicker. See VIDCS-3570.
        const hasBlackVideoFrame = isVideoFrameBlack(videoElementFacade);

        const hasMaxWaitTimeElapsed = metadata.mediaTime > (maxWaitTime / 1000) - 1;

        if (!hasBlackVideoFrame || hasMaxWaitTimeElapsed) {
          // If we have a real video frame, or if we've waited 4s (maybe a webcam cover is down), we proceed.
          return resolve(videoPlaybackEvent);
        }

        // If we haven't seen a non-black video frame, we re-register the callback.
        return videoElementFacade.requestVideoFrameCallback(
          videoFrameCallback
        );
      };

      videoElementFacade.requestVideoFrameCallback(videoFrameCallback);
    };
    videoElementFacadeEvents.on('timeupdate', onVideoPlaybackHandler);
    videoElementFacadeEvents.on('loadedmetadata', onVideoPlaybackHandler);
  });

  return promiseFinally(
    pTimeout(promise, maxWaitTime, () => false),
    () => videoElementFacadeEvents.removeAll()
  );
}

export default waitForVideoToBePlaying;
